import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownUp } from "lucide-react";
import { Dropdown } from "components/common/Dropdown";
import type { DropdownOption } from "components/common/Dropdown/types";
import type { TabSortMode } from "../utils/sortTabList";
import type { TabSortControlProps } from "./types";

/** Full, descriptive labels shown inside the dropdown menu. */
const SORT_MODE_LABEL_KEYS: Record<TabSortMode, string> = {
  smart: "smartSurveyor.reviewPage.sort.smart",
  quantity: "smartSurveyor.reviewPage.sort.quantity",
  name: "smartSurveyor.reviewPage.sort.name",
};

/** Short value shown in the trigger after the constant "Sort:" prefix. */
const SORT_MODE_SHORT_LABEL_KEYS: Record<TabSortMode, string> = {
  smart: "smartSurveyor.reviewPage.sort.smartShort",
  quantity: "smartSurveyor.reviewPage.sort.quantityShort",
  name: "smartSurveyor.reviewPage.sort.nameShort",
};

const SORT_MODES: TabSortMode[] = ["smart", "quantity", "name"];

export const TabSortControl = ({ value, onChange }: TabSortControlProps) => {
  const { t } = useTranslation();

  const options: DropdownOption<TabSortMode>[] = useMemo(
    () =>
      SORT_MODES.map((mode) => ({
        value: mode,
        label: t(SORT_MODE_LABEL_KEYS[mode]),
        triggerLabel: (
          <span className="whitespace-nowrap">
            {`${t("smartSurveyor.reviewPage.sort.label")}: `}
            <span className="inline-block w-14 text-left text-gray-900">
              {t(SORT_MODE_SHORT_LABEL_KEYS[mode])}
            </span>
          </span>
        ),
      })),
    [t],
  );

  return (
    <Dropdown<TabSortMode>
      options={options}
      value={value}
      onChange={onChange}
      startAdornment={
        <ArrowDownUp className="w-3 h-3 text-gray-500 shrink-0" />
      }
      className="shrink-0"
      buttonClassName="px-2 py-2 border-gray-300"
    />
  );
};
